import { useAuth, useClerk } from "@clerk/clerk-react";
import { Button } from "@heroui/react";
import { useEffect } from "react";

import AuthLoadingScreen from "@/components/loading/auth";
import { CONSTANTS } from "@/config/constants";
import { useGetAllChannels } from "@/features/channel/api";
import { useGetOrganization } from "@/hooks/api/organization/useGetOrganization";
import { useGetAllUsers } from "@/hooks/api/user/useGetAllUsers";
import { useGetAllWorkspaces } from "@/hooks/api/workspace/useGetAllWorkspaces";
import { useGetWorkspace } from "@/hooks/api/workspace/useGetWorkspace";
import { useLogo } from "@/hooks/use-logo";
import { queryClient } from "@/main";
import { isMockEnabled } from "@/mock";
import { useMockStore } from "@/mock/store";
import { useGlobalStore } from "@/stores/global";
import { getWorkspaceToActivate } from "@/utils/workspace";

interface BootstrapProviderProps {
  children: React.ReactNode;
}

export const BootstrapProvider = ({ children }: BootstrapProviderProps) => {
  const { userId, isSignedIn } = useAuth();
  const { signOut } = useClerk();

  const {
    activeWorkspaceId,
    setActiveWorkspaceId,
    setActiveWorkspace,
    setOrganization,
    updateLoggedInUser,
    reset,
  } = useGlobalStore();

  const { full: logoFull } = useLogo();

  // Fetch bootstrap data
  const {
    data: organization,
    isLoading: isOrgLoading,
    error: orgError,
    refetch: refetchOrg,
  } = useGetOrganization();

  const {
    data: workspaces,
    isLoading: isWorkspacesLoading,
    error: workspacesError,
    refetch: refetchWorkspaces,
  } = useGetAllWorkspaces();

  const {
    data: users,
    isLoading: isUsersLoading,
    error: usersError,
    refetch: refetchUsers,
  } = useGetAllUsers();

  const {
    isLoading: isChannelsLoading,
    error: channelsError,
    refetch: refetchChannels,
  } = useGetAllChannels();

  const {
    data: workspaceConfig,
    isLoading: isWorkspaceConfigLoading,
    error: workspaceConfigError,
    refetch: refetchWorkspaceConfig,
  } = useGetWorkspace(activeWorkspaceId);

  // Auto-select workspace from localStorage or fallback to first
  useEffect(() => {
    if (!activeWorkspaceId && !isWorkspacesLoading && workspaces?.length) {
      const workspaceToActivate = getWorkspaceToActivate(workspaces);
      if (workspaceToActivate) {
        setActiveWorkspaceId(workspaceToActivate);
      }
    }
  }, [
    activeWorkspaceId,
    isWorkspacesLoading,
    workspaces,
    setActiveWorkspaceId,
  ]);

  // Sync organization to store
  useEffect(() => {
    if (organization) {
      setOrganization(organization);
    }
  }, [organization, setOrganization]);

  // Sync workspace config to store
  useEffect(() => {
    if (workspaceConfig) {
      setActiveWorkspace(workspaceConfig);
    }
  }, [workspaceConfig, setActiveWorkspace]);

  // Sync logged-in user. In mock mode there is no Clerk session, so match the
  // seeded "current" mock user (falling back to the first) instead of an
  // externalId — otherwise loggedInUser stays null and any "me"-scoped UI (e.g.
  // My Activities) never loads.
  useEffect(() => {
    if (isMockEnabled()) {
      if (users?.length) {
        const currentUserId = useMockStore.getState().currentUserId;
        updateLoggedInUser(
          users.find((user) => user.id === currentUserId) ?? users[0],
        );
      }
      return;
    }

    if (userId && isSignedIn && users) {
      updateLoggedInUser(
        users.find((user) => user.externalId === userId) ?? null,
      );
    }
  }, [userId, isSignedIn, users, updateLoggedInUser]);

  // Check for 403 errors - logout user
  const allErrors = [
    orgError,
    workspacesError,
    usersError,
    channelsError,
    workspaceConfigError,
  ];

  const has403Error = allErrors.some((error) => {
    if (!error) return false;
    // Check for 403 status in various error formats
    const err = error as { status?: number; response?: { status?: number } };
    return err.status === 403 || err.response?.status === 403;
  });

  useEffect(() => {
    if (has403Error) {
      reset();
      queryClient.clear();
      signOut();
    }
  }, [has403Error, reset, signOut]);

  if (has403Error) {
    return null;
  }

  // Check for other errors (excluding 403 which is handled above)
  const hasError = allErrors.some((error) => error !== null);

  const handleRetry = () => {
    refetchOrg();
    refetchWorkspaces();
    refetchUsers();
    refetchChannels();
    if (activeWorkspaceId) {
      refetchWorkspaceConfig();
    }
  };

  if (hasError) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-5">
        <img alt={CONSTANTS.BRAND_NAME} height={50} src={logoFull} />
        <p className="text-muted">
          Something went wrong while loading the app.
        </p>
        <Button variant="primary" onPress={handleRetry}>
          Try Again
        </Button>
      </div>
    );
  }

  // Determine if bootstrap is complete
  const isBootstrapping =
    isOrgLoading ||
    isWorkspacesLoading ||
    isUsersLoading ||
    isChannelsLoading ||
    (activeWorkspaceId ? isWorkspaceConfigLoading : false);

  if (isBootstrapping) {
    return <AuthLoadingScreen />;
  }

  return <>{children}</>;
};
